import React from "react";
import {
  getAttachedProductsById,
  getProductListingById,
  getProjectDetailsById,
  getRepositoryByPID,
  getVendorDetailsByOrgId
} from "../../api/pyxis";
import { Bullseye, Spinner } from "@patternfly/react-core";
import { isEmpty } from "lodash";
import ErrorPage, { IErrorPageProps } from "components/shared/ErrorPage/ErrorPage";
import {
  getProjectTypeData,
  ProjectContainerKeys,
  ProjectContainerLinks,
  ProjectContainerTypes,
  ProjectDrupalKeys,
  projectErrorMessages,
  ProjectKeys,
  ProjectTypes,
  TGetProjectTypeDataReturnType
} from "constants/project";
import ArchivedProjectError from "../shared/ArchivedProjectError/ArchivedProjectError";
import Env from "utils/env";
import { ErrorPopup, useAlertContext } from "../shared/AlertComponent/AlertContext";
import { IsOperatorBundleProject } from "utils/projectUtils";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import { CWRouteParams } from "../../constants/router";
import { IProjectRhcertStatusObj } from "../../interfaces/projectRhcert";
import { IProjectDetails } from "../../interfaces/project";
import { getOrgID } from "../../utils/fetch";
import { VendorKeys } from "../../constants/vendor";
import { getPartnerInfo, getProjectChecklist } from "../../api/pfs";
import { getProjectRhcertCase } from "../../api/hydra";
import StatusErrorPage from "components/shared/StatusErrorPage/StatusErrorPage";
import { IAttachableProducts } from "../../interfaces/attachedProducts";
import { IError } from "../../interfaces/operatorTestResults";
import { InitialPaginationValue } from "../../constants/pagination";

export interface ICertificationWorkflowWrapperProps {
  children?: JSX.Element | JSX.Element[] | React.LazyExoticComponent<React.ComponentType<any>> | React.ReactNode;
}

const CertWorkflowAppWrapper: React.FC<ICertificationWorkflowWrapperProps> = (props: ICertificationWorkflowWrapperProps) => {
  const { id, productId: attachedProductId } = useParams<CWRouteParams>();
  const [ errorMessage, setErrorMessage] = React.useState<IErrorPageProps>({} as IErrorPageProps);
  const { setAlertState } = useAlertContext();
  // const {request: fetchProjectRHCertCase } = useFetch(getProjectRhcertCase, {
  //   propagateErrors: true
  // })
  const {isLoading: isProductInfoFetching , error: productIdError} = useQuery(['productDetails'],  ({signal}) => getProductListingById(attachedProductId, signal), {
    enabled: attachedProductId !== Env.productIdStatic,
    onSettled: (data, error: any) => {
      if (error?.status) {
        const errorSummary = projectErrorMessages[error.status]
          ? projectErrorMessages[error.status]
          : projectErrorMessages['default'];
        setErrorMessage(errorSummary);
      }
    }
  });
  const {
    data: projectDetails,
    isLoading: projectDetailsFetching,
    error: responseError
  } = useQuery(
    ['projectDetails'],
    ({signal}) => getProjectDetailsById(id, signal),
    {
      onSettled: (data, error: any) => {
        if (error?.status) {
          const errorSummary = projectErrorMessages[error?.status] ? projectErrorMessages[error?.status] : projectErrorMessages['default'];
          setErrorMessage(errorSummary);
        }
      }
    });
  const {
    isLoading: isVendorDataFetching
  } = useQuery(['vendorDetails'], async ({signal}) => {
    try {
      const vendorData = getVendorDetailsByOrgId(getOrgID());
      return vendorData;
    } catch (e) {
      ErrorPopup(setAlertState, 'Something went wrong while fetching namespace!');
      // Enabling the rest of the settings for the user by setting dummy vendor data
      return {
        [VendorKeys.id]: 'placeholder', // This doesn't matters since label_locked is true, we will not be using it
        [VendorKeys.label]: 'error-fetching-namespace',
        [VendorKeys.label_locked]: true
      };
    }
  }, {
    enabled:  !isEmpty(projectDetails) &&
      [
        ProjectTypes.RHEL,
        ProjectTypes.Containers,
        ProjectTypes.openStackAppContainer,
        ProjectTypes.openStackInfraContainer,
        ProjectTypes.HelmChart
      ].includes(projectDetails?.[ProjectKeys.type])
  });
  useQuery(
    ['repoDetails'],
    ({signal}) => {
      let repoAPIUrl = projectDetails[ProjectKeys.container]
        [ProjectContainerKeys.links][ProjectContainerLinks.repositories].href;
      return getRepositoryByPID(repoAPIUrl, signal);
    },
    {
      enabled: !isEmpty(projectDetails) && IsOperatorBundleProject(projectDetails),
      onError: () => {
        ErrorPopup(setAlertState, 'Something went wrong while fetching repo details')
      }
    });
  const { isLoading: isProjectChecklistLoading } = useQuery(['projectChecklist'], ({signal}) => getProjectChecklist(id, signal));
  const checkIsRHCertEnabled = (projectDetails: IProjectDetails): boolean => {
    let isEnabled: boolean = false;
    const RHCertProjectType: TGetProjectTypeDataReturnType[] = [
      ProjectTypes.openshiftCNF, ProjectTypes.openStackInfraContainer,
      ProjectTypes.openStackInfraNonContainer, ProjectTypes.openStackVnf,
      ProjectTypes.RHEL, "RHEL-NonContainer", ProjectContainerTypes.OPERATOR_BUNDLE_IMAGE,
      ProjectContainerTypes.SCRATCH, ProjectContainerTypes.CONTAINER,
      ProjectTypes.HelmChart
    ];
    const projectType = getProjectTypeData(projectDetails, true);
    if (RHCertProjectType.indexOf(projectType) !== -1) {
      isEnabled = true;
    }
    return isEnabled;
  };
  const isRHCERTEnabled = checkIsRHCertEnabled(projectDetails) || false;
  const isPartnerInfoEnabled = isRHCERTEnabled || projectDetails?.type === ProjectTypes.openShiftVirtualization || false;
  const {
    isLoading: isRHCertFetching
  } = useQuery<IProjectRhcertStatusObj>(["projectRHCertStatus"],
    async ({ signal }) => {
      try {
        const response = await getProjectRhcertCase(id, signal);
        return {
          responseStatus: 200,
          response
        };
      } catch (e) {
        return {
          responseStatus: e?.status,
          response: e
        };
      }
    },
    {
      enabled: isRHCERTEnabled
    });
  const {
    isLoading: isSubsLoading
  } = useQuery(["partnerInfo"], ({ signal }) => getPartnerInfo(signal),
    {
      enabled: isPartnerInfoEnabled
  });
  // Used in associated product list and other places
  useQuery<IAttachableProducts, IError>(["attachedProductsToCert", InitialPaginationValue], async ({ signal }) => {
    return await getAttachedProductsById(projectDetails?.[ProjectKeys.id], {
      page: InitialPaginationValue.page - 1,
      page_size: InitialPaginationValue.perPage
    }, {}, signal);
  }, {
    enabled: !isEmpty(projectDetails)
  });
  const isFetching = (isProductInfoFetching || projectDetailsFetching || isRHCertFetching || isVendorDataFetching || isProjectChecklistLoading || isSubsLoading);
  const includeProjectType = () => {
    return ["Containers", "Helm Chart", "RHEL", "OpenStack-infra-container", "OpenStack-infra-noncontainer", "OpenStack-vnf", 'OpenStack-app-container', ProjectTypes.openshiftCNF,
        ProjectTypes.openShiftVirtualization].includes(projectDetails?.[ProjectKeys.type]) ||
      (projectDetails[ProjectKeys.type] === 'OpenStack' && !isEmpty(projectDetails[ProjectKeys.container]));
  }

  const redirectToLegacyRoute = () => {
    const relationID = projectDetails?.[ProjectKeys.drupal]?.[ProjectDrupalKeys.relation];
    window.location.href = `${Env.ConnectPortalUrls[Env.getEnvName()]}project/${relationID}/view`;
  }

  const renderWrapper = () => {
    if (isFetching) {
      return (<Bullseye><Spinner size="xl" /></Bullseye>)
    } else if (responseError || productIdError) {
      return errorMessage.errorCode === 404 ? (
        <StatusErrorPage />
      ) : (
        <ErrorPage
          title={errorMessage.title}
          description={errorMessage.description}
          errorCode={errorMessage.errorCode}
          shortDescription={errorMessage.shortDescription}
          suggestionLinks={errorMessage.suggestionLinks}
        />
      );
    } else if (!isEmpty(projectDetails)) {
      if (includeProjectType()) {
        if (projectDetails[ProjectKeys.projectStatus] === "archived") {
          return (<ArchivedProjectError />)
        } else {
          return (<div>{props.children}</div>)
        }
      } else {
        redirectToLegacyRoute();
      }
    }
  }

  return (<>{renderWrapper()}</>);
};

export default CertWorkflowAppWrapper;

